import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { functions, auth } from '../firebase';
import { Loader } from 'lucide-react';
import { devLog, devError } from '../utils/debugLogger';

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Waiting for authentication...');
  const [error, setError] = useState(null);
  const processedRef = useRef(false);

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    const state = searchParams.get('state');
    
    devLog('[OAuth] OAuthCallback mounted');
    devLog('[OAuth] URL params:', { code: code ? `${code.substring(0, 20)}...` : null, error: errorParam, state });

    if (errorParam) {
      devError('[OAuth] Error from Google:', errorParam);
      setError('Google access denied or failed.');
      return;
    }

    if (!code) {
      devError('[OAuth] No authorization code in URL');
      setError('No authorization code found.');
      return;
    }
    
    devLog('[OAuth] Authorization code received, waiting for Firebase auth...');

    // Wait for Firebase Auth to restore the session
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        devLog('[OAuth] Waiting for Firebase auth to restore...');
        // Still waiting for auth to restore
        return;
      }

      devLog('[OAuth] Firebase user authenticated:', firebaseUser.uid);
      
      if (processedRef.current) {
        devLog('[OAuth] Already processed, skipping');
        return;
      }
      processedRef.current = true;

      try {
        devLog('[OAuth] Starting token exchange...');
        setStatus('Connecting to Google Calendar...');
        const exchangeFn = httpsCallable(functions, 'exchangeGoogleAuthCode');
        const exchangeResult = await exchangeFn({ code });
        devLog('[OAuth] Token exchange successful:', exchangeResult.data);
        
        devLog('[OAuth] Starting initial calendar sync...');
        setStatus('Syncing your calendar...');
        const syncFn = httpsCallable(functions, 'syncGoogleCalendar');
        const syncResult = await syncFn();
        devLog('[OAuth] Sync complete:', syncResult.data);

        // Success! Redirect back to events
        devLog('[OAuth] Redirecting to /events?success=true');
        navigate('/events?success=true', { replace: true });
      } catch (err) {
        devError('[OAuth] Error during token exchange/sync:', err);
        devError('[OAuth] Error code:', err.code);
        devError('[OAuth] Error details:', err.details);
        setError(`Failed to connect: ${err.message}`);
        processedRef.current = false;
      }
    });

    return () => unsubscribe();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white p-4">
      <div className="max-w-md w-full text-center space-y-6">
        {error ? (
          <div className="p-4 rounded-md bg-red-500/10 border border-red-500/20 text-red-400">
            <h3 className="text-lg font-medium mb-2">Connection Failed</h3>
            <p>{error}</p>
            <button 
              onClick={() => navigate('/events')}
              className="mt-4 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            >
              Return to App
            </button>
          </div>
        ) : (
          <>
            <Loader className="w-12 h-12 animate-spin mx-auto opacity-60" />
            <h2 className="text-xl font-light">{status}</h2>
            <p className="text-white/40 text-sm">Please do not close this window.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
